import { Link, useParams } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { PageHero, CTABand, Rise, Eyebrow, ArrowCard } from "@/components/chrome";
import { SERVICES } from "@/data/services";
import { findProvince, paths } from "@/lib/seo";
import { provinceContent } from "@/lib/content";
import NotFound from "@/pages/NotFound";

export default function ProvincePage() {
  const { provinceSlug } = useParams();
  const province = findProvince(provinceSlug);
  if (!province) return <NotFound />;
  const content = provinceContent(province);

  return (
    <div data-testid="province-page">
      <PageHero prefix="province"
        crumbs={[{ name: "Home", path: "/" }, { name: "Locations", path: "/locations" }, { name: province.name }]}
        eyebrow={`${province.code} — ${province.cities.length} ${province.cities.length === 1 ? "city" : "cities"} served`}
        title={content.h1}
        lead={province.blurb} />

      <section className="mx-auto max-w-6xl px-5 py-14 sm:py-16" data-testid="province-overview">
        <div className="max-w-3xl space-y-5">
          {content.intro.map((p, i) => <p key={i} className="leading-relaxed text-slate-600 sm:text-lg">{p}</p>)}
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white" data-testid="province-cities">
        <div className="mx-auto max-w-6xl px-5 py-14 sm:py-16">
          <Rise><Eyebrow>Cities in {province.name}</Eyebrow>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Choose your {province.name} city
            </h2></Rise>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {province.cities.map((c, i) => (
              <Rise key={c.slug} delay={(i % 3) * 0.04}>
                <Link to={paths.city(province.slug, c.slug)} data-testid={`province-city-${c.slug}`}
                  className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-[#FAFAFC] p-6 transition-all duration-300 hover:-translate-y-1 hover:border-cyan-600/40">
                  <div className="flex items-center justify-between">
                    <MapPin className="h-4 w-4 text-cyan-700" />
                    {c.hq && <span className="rounded-full bg-cyan-100 px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-cyan-800">HQ</span>}
                  </div>
                  <h3 className="mt-4 font-display text-lg font-semibold tracking-tight text-slate-900">{c.name}</h3>
                  <p className="mt-1 text-sm text-slate-500">Population {c.pop} · 12 services available</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-700">
                    Cleaning services in {c.name} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </Link>
              </Rise>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-14 sm:py-16" data-testid="province-services">
        <Rise><Eyebrow>Services in {province.name}</Eyebrow>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            All 12 services, available across {province.name}
          </h2></Rise>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s) => (
            <ArrowCard key={s.slug} to={paths.serviceCity(province.slug, province.cities[0].slug, s.slug)}
              icon={s.icon} title={`${s.short} in ${province.name}`} desc={s.tagline}
              testid={`province-service-${s.slug}`} />
          ))}
        </div>
      </section>

      <CTABand title={`Book a cleaning anywhere in ${province.name}`}
        lead="One call reaches every crew in the province. Flat-rate quotes, usually within one business hour."
        prefix="province-cta" />
    </div>
  );
}
